import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Trail, Billboard, Text } from "@react-three/drei";
import * as THREE from "three";
import { FONTS } from "../game/theme";

// A glowing answer orb that arcs from a player to the board, carrying the answer text.
// Calls onArrive(orb) exactly once when it lands.
const Orb = ({ from, to, color = "#ffffff", label = "", duration = 0.7, onArrive, data }) => {
  const group = useRef();
  const t = useRef(0);
  const arrived = useRef(false);

  const { a, b, mid } = useMemo(() => {
    const a = new THREE.Vector3(...from);
    const b = new THREE.Vector3(...to);
    const mid = a.clone().lerp(b, 0.5);
    mid.y += 2.6; // arc height
    return { a, b, mid };
  }, [from, to]);

  useFrame((_, dt) => {
    if (!group.current || arrived.current) return;
    t.current = Math.min(1, t.current + dt / duration);
    const k = t.current;
    // Quadratic bezier
    const p = new THREE.Vector3()
      .addScaledVector(a, (1 - k) * (1 - k))
      .addScaledVector(mid, 2 * (1 - k) * k)
      .addScaledVector(b, k * k);
    group.current.position.copy(p);
    const s = 1 + Math.sin(k * Math.PI) * 0.4;
    group.current.scale.setScalar(s);
    if (k >= 1 && !arrived.current) {
      arrived.current = true;
      onArrive && onArrive(data);
    }
  });

  return (
    <group ref={group} position={from}>
      <Trail width={2} length={4.5} color={color} attenuation={(w) => w * w}>
        <mesh>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={2.5}
            toneMapped={false}
          />
        </mesh>
      </Trail>
      <pointLight color={color} intensity={10} distance={5} />
      {label && (
        <Billboard position={[0, 0.55, 0]}>
          <Text
            font={FONTS.tech}
            fontSize={0.38}
            color="#ffffff"
            outlineWidth={0.028}
            outlineColor="#000000"
            anchorX="center"
            maxWidth={5}
          >
            {label}
          </Text>
        </Billboard>
      )}
    </group>
  );
};

export default Orb;
